// Edit, approve, view, or otherwise interact with an Invoice

import { Block, Description, MarkEmailRead, Preview, Savings, Send, Task, TaskAlt, Update } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, MenuItem, Stack, TextField, Typography } from "@mui/material";
import TopBar from "components/dashboardParts/TopBar";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import APIRequest, { APIError, BuildAPIURL } from "system/APIRequest";
import { permissionTest } from "system/InfegyUser";
import config from "appConfig";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

// Used for pretty printing relative date strings
dayjs.extend(relativeTime);

function formatCurrency(amt: number, currency?: string): string {
  const v = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency?.toUpperCase() || "USD",
  }).format(amt);
  return v.endsWith(".00") ? v.slice(0, -3) : v;
}

function formatDate(isostr: string): string {
  let _d = new Date(isostr);
  _d = new Date(_d.getUTCFullYear(), _d.getUTCMonth(), _d.getUTCDate(), 0, 0, 0);
  return _d.toLocaleDateString(undefined, config().dateFormatOptions);
}

interface InvoiceLineItemDiscount {
  readonly type: string;
  readonly value: number;
  readonly amount: number;
}

interface InvoiceLineItem {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly unit: string;
  readonly price: number;
  readonly discount: InvoiceLineItemDiscount;
  readonly quantity?: number;
  readonly base_price: number;  
}

interface InvoiceApprovalUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
}

interface Invoice {
  readonly id: string;
  readonly order_id: string;
  readonly type: string;
  readonly currency: string;
  readonly account_name?: string;
  readonly bill_to_name: string;
  readonly bill_to_emails?: Array<string>;
  readonly date_issued: string;
  readonly date_due: string;
  readonly net_due: number;
  readonly po_number?: string;
  readonly billing_address?: string;
  readonly memo?: string;
  readonly number: string;
  readonly approved_at?: string;
  readonly approved_by_user?: InvoiceApprovalUser;
  readonly sent_at?: string;
  readonly status: string;
  readonly value: number,
  readonly amount_paid?: number,
  readonly payment_updated_at?: string,
  readonly payment_source?: string,
  readonly payment_notes?: string,
  readonly payment_link?: string;
  readonly subscription_line_items?: Array<InvoiceLineItem>;
  readonly api_line_items?: Array<InvoiceLineItem>;
  readonly onetime_line_items?: Array<InvoiceLineItem>;
}

// The modifiable properties of an invoice
interface InvoiceModifiable {
  bill_to_name: string;
  bill_to_emails?: string;
  po_number?: string;
  billing_address?: string;
  memo?: string;
}

// Little utility to make links based on API urls
const makeLink = (endpoint: string) => {
  return BuildAPIURL(endpoint).replace('/api/', '/');
}

export default function InvoicePage() {
  const { invoiceId } = useParams() as { invoiceId?: string };
  
  const [invoice, setInvoice] = useState<Invoice | undefined>(undefined);
  const [invoiceModifiable, setInvoiceModifiable] = useState<InvoiceModifiable>({bill_to_name:''});
  const [initError, setInitError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [payDialogOpen, setPayDialogOpen] = useState<boolean>(false);
  const [resendDialogOpen, setResendDialogOpen] = useState<boolean>(false);
  
  const loadInvoice = () => {
    APIRequest(`billing/invoice/${invoiceId}`, {}, {}, true).then((_invoice: Invoice) => {
      setInvoice(_invoice);
      setInvoiceModifiable({
        bill_to_name: _invoice.bill_to_name,
        bill_to_emails: _invoice.bill_to_emails ? _invoice.bill_to_emails.join(', ') : undefined,
        po_number: _invoice.po_number,
        billing_address: _invoice.billing_address,
        memo: _invoice.memo
      });
    }).catch((reason: APIError) => {
      console.error("Error loading invoice", reason);
      setInitError(`Error loading invoice: ${reason}`);
    });
  }
  
  useEffect(() => {
    loadInvoice();
  }, []);
  
  const approveInvoice = () => {
    setSaving(true);
    setSaveError(undefined);
    
    APIRequest(`billing/invoice/${invoiceId}/approve`, {}, { method: 'POST' }, true).then(() => {
      setInvoice(undefined);
      loadInvoice();
    }).catch((error: APIError) => {
      console.error('API error approving invoice', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaving(false));
  }
  
  const markSent = () => {
    setSaving(true);
    setSaveError(undefined);
    
    APIRequest(`billing/invoice/${invoiceId}/marksent`, {}, { method: 'POST' }, true).then(() => {
      if(invoice) {
        setInvoice({ ...invoice, sent_at: dayjs(new Date()).toISOString() });
      }
    }).catch((error: APIError) => {
      console.error('API error voiding invoice', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaving(false));
  }
  
  const voidInvoice = () => {
    setSaving(true);
    setSaveError(undefined);
    
    APIRequest(`billing/invoice/${invoiceId}`, {}, { method: 'DELETE' }, true).then(() => {
      if(invoice) {
        setInvoice({ ...invoice, status: 'void' });
      }
    }).catch((error: APIError) => {
      console.error('API error voiding invoice', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaving(false));
  }
  
  const saveChanges = () => {
    setSaving(true);
    setSaveError(undefined);
    
    APIRequest(`billing/invoice/${invoiceId}`, {}, { method: 'PUT', body: JSON.stringify(invoiceModifiable) }, true).then(() => {
      if(invoice) {
        let bill_to_emails = invoiceModifiable.bill_to_emails?.split(',').map(s => s.trim()).filter(s => s);
        if (bill_to_emails?.length === 0) {
          bill_to_emails = undefined;
        }
        
        setInvoice({ ...invoice, 
          bill_to_name: invoiceModifiable.bill_to_name,
          bill_to_emails: bill_to_emails,
          po_number: invoiceModifiable.po_number,
          billing_address: invoiceModifiable.billing_address,
          memo: invoiceModifiable.memo
        });
        
        if(bill_to_emails) {
          setInvoiceModifiable({ ...invoiceModifiable, bill_to_emails: bill_to_emails.join(', ') });
        } else {
          setInvoiceModifiable({ ...invoiceModifiable, bill_to_emails: '' });
        }
      }
    }).catch((error: APIError) => {
      console.error('API error saving invoice', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaving(false));
  }
  
  const handleRecordPayment = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    let formParseFailed = false;
    
    const formData = new FormData(event.currentTarget);
    const formJson = JSON.stringify(Object.fromEntries(formData.entries()), (key, value) => {
      if (value === "") return undefined;
      if (key == "value") {
        const v = parseFloat(value.replace(/[^0-9.-]/g, ''));
        if(!v || Number.isNaN(v) || !Number.isFinite(v) || v < 0.01) {
          setSaveError("Amount was not parsed as a valid number");
          formParseFailed = true;
        }
        return v;
      }
      return value;
    });
    
    if(formParseFailed) {
      setSaving(false);
      return;
    }
    
    setSaving(true);
    setSaveError(undefined);
    
    APIRequest(`billing/invoice/${invoiceId}/pay`, {}, { method: "POST", body: formJson }).then(() => {
      setPayDialogOpen(false);
      loadInvoice();
    }).catch((error: APIError) => {
      console.error('API error recording payment', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaving(false));
  };
  
  const handleResendEmail = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const addresses = (formData.get('addresses') as string) || '';
    
    const emails = addresses.split(',').map(s => s.trim()).filter(s => s);
    if(emails.length === 0) {
      setSaveError('No email addresses provided');
    }
    
    setSaving(true);
    setSaveError(undefined);
    
    APIRequest(`billing/invoice/${invoiceId}/email`, {}, { method: 'POST', body: emails.join(',') }, true).then(() => {
      // Reload, to update invoice state (like we should have sent_at now set)
      window.location.reload();
    }).catch((error: APIError) => {
      console.error('API error re-sending email', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaving(false));
  };

  const handleResetIssueDate = () => {
    setSaving(true);
    setSaveError(undefined);

    APIRequest(`billing/invoice/${invoiceId}/updatedate`, {}, { method: 'POST' }, true).then(() => {
      // Reload, to update invoice state
      window.location.reload();
    }).catch((error: APIError) => {
      console.error('API error updating issue date', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaving(false));
  };
  
  const hasModifications = () => {
    return !(invoice?.bill_to_name === invoiceModifiable.bill_to_name &&
      ((invoice?.bill_to_emails?.join(', ') === invoiceModifiable.bill_to_emails) || (!invoice?.bill_to_emails && !invoiceModifiable.bill_to_emails)) &&
      invoice?.po_number === invoiceModifiable.po_number &&
      invoice?.billing_address === invoiceModifiable.billing_address &&
      invoice?.memo === invoiceModifiable.memo);
  }
  
  // Initialization isn't done yet, or failed
  if (initError || !invoice) {
    return (
      <>
        <TopBar title="New Account Order" showLogo={true}></TopBar>
        <Alert style={{ margin: "1rem" }} severity={initError ? "error" : "info"}>
          {initError && <>{`Initialization error: ${initError}`}</>}
          {!initError && <><CircularProgress size="1rem" /> {"Initializing..."}</>}
        </Alert>
      </>
    );
  }
  
  const invoiceHasEmail = invoice.bill_to_emails && invoice.bill_to_emails?.length;
  const isEditable = !saving && invoice.status !== 'paid' && invoice.status !== 'void';
  const statusColor = invoice.status === 'paid' ? 'success' : (invoice.status === 'void' ? 'error' : (invoice.status === 'draft' ? 'grey' : 'blue'));
  const displayName = invoice.account_name ? invoice.account_name : invoice.bill_to_name;
  
  return (
    <Box sx={{ maxWidth: "1000px", margin: "0 auto", position: "relative", pl: 2, pr: 2 }}>
      <TopBar title={`${displayName} ${invoice.type === "api-usage" ? "🤖 API Usage":"🔁"} Invoice`} showLogo={true} />

      <Stack
        sx={{
          alignItems: "stretch",
          gap: 1
        }}>
        {invoice.status !== 'void' &&
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "end"
            }}>
          
            <Button
              startIcon={<Preview />}
              href={makeLink(`billing/invoice/${invoiceId}`)}
              target='_blank'
            >
              View Invoice Page
            </Button>
          
            {permissionTest("super_admin") && !invoice.approved_at &&
              <Button variant="outlined" color="success" loading={saving} size='large'
                disabled={saving || hasModifications()}
                onClick={() => approveInvoice()}
                startIcon={invoiceHasEmail ? <MarkEmailRead /> : <TaskAlt />}
              >
                {invoiceHasEmail && <>Approve and Send</>}
                {!invoiceHasEmail && <>Approve</>}
              </Button>
            }

            {permissionTest("super_admin") && !invoice.sent_at &&
              <Button variant="outlined" color="secondary" loading={saving} size='large'
                disabled={saving}
                onClick={() => handleResetIssueDate()}
                startIcon={<Update />}
              >
                Reset Issue Date
              </Button>
            }
            
            {permissionTest("super_admin") && invoice.approved_at && !invoice.sent_at &&
              <Button variant="outlined" color="warning" loading={saving} size='large'
                disabled={saving}
                onClick={() => markSent()}
                startIcon={<Task />}
              >
                Mark as Sent
              </Button>
            }
            
            {permissionTest("super_admin") && invoice.approved_at && invoice.status !== 'paid' &&
              <>
                <Button variant="outlined" color="success" loading={saving} size='large'
                  disabled={saving}
                  onClick={() => setPayDialogOpen(true)}
                  startIcon={<Savings />}
                >
                  Record Payment
                </Button>
                
                <Dialog open={payDialogOpen} onClose={() => { if (saving) return; setPayDialogOpen(false); }} scroll="paper">
                  <DialogTitle>Record Payment</DialogTitle>
                  <DialogContent>
                    <DialogContentText sx={{
                      paddingBottom: 1
                    }}>
                      Record an offline payment, such as a mailed check<br />
                      So long as this is at or near the full amount, we will mark the invoice as paid.
                    </DialogContentText>
                    <form onSubmit={handleRecordPayment} id="override-signing-form">
                      <TextField select fullWidth variant="standard" margin="dense" required autoFocus
                        id="source" name="source"
                        label="Source" defaultValue="bank_transfer"
                      >
                        <MenuItem value="check">Check</MenuItem>
                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      </TextField>
                      
                      <TextField fullWidth variant="standard" margin="dense" required
                         id="value" name="value"
                        label="Value"
                      />
                      
                      <TextField fullWidth variant="filled" margin="dense" multiline rows={3} required
                         id="notes" name="notes"
                        label="Payment Details (please note anything relevant, like where, how?)"
                      />
                    </form>
                  </DialogContent>
                  {saveError &&
                    <DialogContentText color="error" sx={{
                      paddingLeft: 3
                    }}>
                      {saveError}
                    </DialogContentText>
                  }
                  <DialogActions>
                    <Button onClick={() => setPayDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button type="submit" form="override-signing-form" loading={saving}>
                      Save Payment
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            }
          
            <Button
              startIcon={<Description />}
              href={makeLink(`billing/invoice/${invoiceId}?pdf=1`)}
              target='_blank'
            >
              Invoice PDF
            </Button>
          </Stack>
        }
        
        <Stack direction={{ s: 'column', md: 'row' }} sx={{
          gap: 2
        }}>
          <Card sx={{ flexShrink: 0, minWidth: "10em"}}>
            <CardContent>
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'fit-content(100%) fit-content(100%)' }}>
                  <Typography variant='h4'>Invoice number</Typography>
                  <Typography variant='h4'>{invoice.number}</Typography>
                  
                  <Typography variant='h4'>Type</Typography>
                  <Typography variant='h4'>{invoice.type === 'api-usage' ? "🤖" : "🔁"} {invoice.type}</Typography>
                  
                  <Typography variant='h4'>Status</Typography>
                  <Typography variant='h5' color={statusColor}>{invoice.status.toUpperCase()}</Typography>
                  
                  <Typography variant='h4'>Value</Typography>
                  <Typography variant='h5'>{formatCurrency(invoice.value, invoice.currency)}</Typography>
                  
                  <Typography variant='h4'>Amount paid</Typography>
                  <Typography variant='h5'>{invoice.amount_paid ? formatCurrency(invoice.amount_paid, invoice.currency) : 'none'}</Typography>
                  
                  <Typography variant='h4'>Date of issue</Typography>
                  <Typography variant='h5'>{formatDate(invoice.date_issued)}</Typography>
                  
                  <Typography variant='h4'>Approved at</Typography>
                  <Typography variant={invoice.approved_at ? 'h5' : 'body1'}>{invoice.approved_at ? `${formatDate(invoice.approved_at)}` : 'never'}</Typography>
                  
                  <Typography variant='h4'>Approved by</Typography>
                  <Typography variant={invoice.approved_by_user ? 'h5' : 'body1'}>{invoice.approved_by_user ? invoice.approved_by_user.name : 'nobody'}</Typography>
                  
                  <Typography variant='h4'>Sent at</Typography>
                  <Typography variant={invoice.sent_at ? 'h5' : 'body1'}>{invoice.sent_at ? `${formatDate(invoice.sent_at)}` : 'never'}</Typography>
                  
                  <Typography variant='h4'>Date due</Typography>
                  <Typography variant='h5'>{`${formatDate(invoice.date_due)}`}<Typography> {`(Net ${invoice.net_due}; ${dayjs(invoice.date_due).fromNow()})`}</Typography></Typography>
                  
                  <Typography variant='h4'>Paid at</Typography>
                  <Typography variant={invoice.payment_updated_at ? 'h5' : 'body1'}>{invoice.payment_updated_at ? `${formatDate(invoice.payment_updated_at)}` : 'never'}</Typography>
                  
                  {invoice.payment_updated_at && <>
                    <Typography variant='h4'>Payment Method</Typography>
                    <Typography variant={invoice.payment_source ? 'h5' : 'body1'}>{invoice.payment_source ? `${invoice.payment_source}` : 'none'}</Typography>
                  </>}
              </Box>
              <Stack
                sx={{
                  gap: 1,
                  mt: 1
                }}>
                {invoice.payment_updated_at && invoice.payment_notes && <>
                  <Typography variant='h4'>Payment Notes</Typography>
                  <Typography variant='body1' sx={{
                    maxWidth: '20em'
                  }}>{invoice.payment_notes}</Typography>
                </>}
                
                <Link href={`/admin/billing/order/${invoice.order_id}`}>Source Order editor</Link>
                {invoice.payment_link && invoice.status !== 'paid' &&
                  <Link href={invoice.payment_link}>Online payment link</Link>
                }
                {!invoice.payment_link && invoice.approved_at && invoice.status !== 'paid' &&
                  <Typography>(no payment link)</Typography>
                }
              </Stack>
            </CardContent>
          </Card>
          
          <Card sx={{ flexGrow: 1 }}>
            <CardContent>
              <Stack
                sx={{
                  gap: 1,
                  minWidth: "40em",
                  width: "100%"
                }}>
                <Typography variant='h4'>Bill To</Typography>
                
                <Typography variant='subtitle2'>Bill To Name</Typography>
                <TextField required variant="outlined" sx={{ width: "50%", maxWidth: "20em"}}
                  disabled={!isEditable}
                  value={invoiceModifiable.bill_to_name || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setInvoiceModifiable({ ...invoiceModifiable, bill_to_name: e.target.value });
                  }}
                />
                
                <Typography variant='subtitle2'>Email(s)</Typography>
                <TextField variant="outlined" sx={{ width: "50%", maxWidth: "20em"}}
                  disabled={!isEditable || !!invoice.approved_at}
                  placeholder="No email, invoice will not send"
                  value={invoiceModifiable.bill_to_emails || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setInvoiceModifiable({ ...invoiceModifiable, bill_to_emails: e.target.value });
                  }}
                />
                
                <Typography variant='subtitle2' sx={{
                  mt: 1
                }}>PO number</Typography>
                <TextField variant="outlined" sx={{ width: "50%", maxWidth: "20em"}}
                  disabled={!isEditable}
                  value={invoiceModifiable.po_number || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setInvoiceModifiable({ ...invoiceModifiable, po_number: e.target.value });
                  }}
                />
                
                <Typography variant='subtitle2' sx={{
                  mt: 1
                }}>Billing Address</Typography>
                <TextField variant="outlined" multiline sx={{ width: "50%", maxWidth: "20em"}}
                  disabled={!isEditable}
                  value={invoiceModifiable.billing_address || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setInvoiceModifiable({ ...invoiceModifiable, billing_address: e.target.value });
                  }}
                />
                
                <Typography variant='subtitle2' sx={{
                  mt: 1
                }}>Memo</Typography>
                <TextField variant="outlined" multiline sx={{ width: "100%", maxWidth: "40em"}}
                  disabled={!isEditable}
                  value={invoiceModifiable.memo || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setInvoiceModifiable({ ...invoiceModifiable, memo: e.target.value });
                  }}
                />
                
              </Stack>
            </CardContent>
          </Card>
        </Stack>
        
        {/* Interaction buttons on bottom, to delete (only when created and not signed), approve (when created and not approved), and create or update (when not approved)  */}
        <Stack
          sx={{
            width: "100%",
            alignItems: "end",
            gap: 2
          }}>
          {saveError &&
            <Typography color="error" variant="h5" sx={{
              mb: 1
            }}>{saveError}</Typography>
          }
          
          <Stack
            direction="row"
            sx={{
              width: "100%",
              justifyContent: (invoice.status === 'draft' || invoice.status === 'open') ? "space-between" : "end",
              gap: 2
            }}>
            {(invoice.amount_paid || 0) < 0.01 && invoice.status !== 'void' && invoice.status !== 'paid' &&
              <>
              <Button variant="outlined" color="error" loading={saving} startIcon={<Block />}
                onClick={() => voidInvoice()}
              >
                Void
              </Button>
              
              <Button variant="outlined" color="success" loading={saving}
                disabled={saving || !hasModifications()}
                onClick={() => saveChanges()}
              >
                Update Invoice
              </Button>
              </>
            }
            {invoice.status === 'open' && <>
              <Button variant="outlined" color="warning" loading={saving}
                startIcon={<Send />}
                disabled={saving || hasModifications()}
                onClick={() => setResendDialogOpen(true)}
              >
                {invoice.sent_at ? "Re-send by Email" : "Send by Email"}
              </Button>
              
              <Dialog open={resendDialogOpen} onClose={() => { if (saving) return; setResendDialogOpen(false); }} scroll="paper">
                <DialogTitle>{invoice.sent_at ? "Re-send" : "Send"} Invoice by e-mail</DialogTitle>
                <DialogContent>
                  <DialogContentText sx={{
                    paddingBottom: 1
                  }}>
                    This will send the invoice by email, with attached PDF, to the specified addresses.
                  </DialogContentText>
                  <form onSubmit={handleResendEmail} id="override-signing-form">
                    <TextField fullWidth variant="standard" margin="dense" required autoFocus
                      id="addresses" name="addresses"
                      label="Email(s) comma-separated" defaultValue={invoice.bill_to_emails || ''}
                    />
                  </form>
                </DialogContent>
                {saveError &&
                  <DialogContentText color="error" sx={{
                    paddingLeft: 3
                  }}>
                    {saveError}
                  </DialogContentText>
                }
                <DialogActions>
                  <Button onClick={() => setResendDialogOpen(false)} disabled={saving}>Cancel</Button>
                  <Button type="submit" form="override-signing-form" loading={saving}>
                    Send Email(s)
                  </Button>
                </DialogActions>
              </Dialog>
            </>}
          </Stack>
        </Stack>
          
      </Stack>
    </Box>
  );
      
}